// src/features/S1Metadata/new.tsx

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { Clients } from "../../lib/clients";
import { ClientTables } from "../../lib/clientTables";
import { FormActionsNew } from "@/features/FormCard/Actions";
import {
  AutocompleteAsyncField,
  InputField,
  SelectField,
} from "@/features/FormCard/Fields";
import {
  CancelConfirmationModal,
  ConfirmationModal,
} from "@/features/FormCard/Modals";
import { preprocessTextAsyncAutocompleteField } from "@/features/FormCard/utils";

import { useEditForm } from "@/features/Edit/hooks";
import { NewDiff } from "@/features/FormCard/NewDiff";

// Import options from filter.tsx — do not modify filter.tsx
import { fieldKeyOptions, fieldValueOptions } from "./filter";

const client = "s1-quantilia";
const table_name = "s1_metadata";
const DEFAULT_OWNER_TYPE = "Strategy";

// Zod schema: owner and field_key are required; field_value is optional
const FormSchema = z.object({
  // Display field for autocomplete
  owner_name: z.preprocess(
    (e: any) => preprocessTextAsyncAutocompleteField(e),
    z
      .union([
        z.string(),
        z.object({
          id: z.number(),
          name: z.string(),
          text: z.string(),
        }),
      ])
      .optional(),
  ),
  // ID field
  owner_id: z.coerce.number().min(1, "Owner is required"),
  owner_type: z.string().min(1, "Owner Type is required"),
  field_key: z.string().min(1, "Field Key is required"),
  field_value: z.string().optional().nullable(),
});

export function S1MetadataNew() {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
  } = useEditForm({
    client,
    table_name,
    FormSchema,
    isNew: true,
    disable_translations_request: true,
  });

  useEffect(() => {
    form.setValue("owner_type", DEFAULT_OWNER_TYPE);
  }, [form]);

  const handleResetWithDefaults = () => {
    handleReset();
    form.setValue("owner_type", DEFAULT_OWNER_TYPE);
  };

  const navigateToList = () => navigate(-1);

  return (
    <div className="grid place-items-center h-full w-full">
      <div className="flex justify-center w-full max-w-card">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                New S1 Metadata
              </CardHeader>
              <Divider />

              <CardBody className="flex flex-col gap-4">
                {/* Row 1: owner fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AutocompleteAsyncField
                    name="owner_name"
                    label={t(`${table_name}.columns.owner_name`, {
                      ns: "tables",
                      defaultValue: "Owner",
                    })}
                    base_url={`${Clients.S1_QUANTILIA}/${ClientTables.S1_META_DATA}`}
                    column_name="name"
                    id_column="owner_id"
                    control={form.control}
                    isRequired
                  />
                  <InputField
                    name="owner_type"
                    label={t(`${table_name}.columns.name`, {
                      ns: "tables",
                      defaultValue: "Owner Type",
                    })}
                    isRequired
                    control={form.control}
                  />
                </div>
                {/* Row 2: metadata fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectField
                    name="field_key"
                    label={t(`${table_name}.columns.field_key`, {
                      ns: "tables",
                      defaultValue: "Field Key",
                    })}
                    control={form.control}
                    options={fieldKeyOptions}
                    isRequired
                  />
                  <SelectField
                    name="field_value"
                    label={t(`${table_name}.columns.field_value`, {
                      ns: "tables",
                      defaultValue: "Field Value",
                    })}
                    control={form.control}
                    options={fieldValueOptions}
                    allow_empty_option
                  />
                </div>
              </CardBody>

              <CardFooter>
                <FormActionsNew
                  onReset={handleResetWithDefaults}
                  onDelete={openDeleteModal}
                />
              </CardFooter>
            </form>
          </Form>

          {/* Confirmation before creating */}
          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_create")}
          >
            {changedFields ? NewDiff(changedFields, []) : "No changes detected"}
          </ConfirmationModal>

          {/* Cancel creation modal */}
          <CancelConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onCancel={navigateToList}
          />
        </Card>
      </div>
    </div>
  );
}
